import { useState } from 'react';
import { PresentationTemplate } from '../../../mocks/presentationTemplates';

interface FavoritesDrawerProps {
  favorites: string[];
  allTemplates: PresentationTemplate[];
  onClose: () => void;
  onUnfavorite: (id: string) => void;
  onPreview: (template: PresentationTemplate) => void;
  onUse: () => void;
}

export default function FavoritesDrawer({
  favorites,
  allTemplates,
  onClose,
  onUnfavorite,
  onPreview,
  onUse,
}: FavoritesDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const savedTemplates = favorites
    .map(id => allTemplates.find(t => t.id === id))
    .filter((t): t is PresentationTemplate => Boolean(t));

  const filtered = savedTemplates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const badgeColors: Record<string, string> = {
    Popular: 'bg-amber-100 text-amber-700',
    New: 'bg-emerald-100 text-emerald-700',
    Trending: 'bg-rose-100 text-rose-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className="w-[420px] bg-white flex flex-col h-full shadow-2xl animate-[slideInRight_0.3s_cubic-bezier(0.22,1,0.36,1)]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-xl flex items-center justify-center">
              <i className="ri-heart-fill text-white text-base"></i>
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-slate-900">Saved Templates</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {savedTemplates.length} template{savedTemplates.length !== 1 ? 's' : ''} saved
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Search inside drawer */}
        {savedTemplates.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-100 flex-shrink-0">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
              <input
                type="text"
                placeholder="Filter saved templates..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-200/40 transition-colors"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          {savedTemplates.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center h-full px-8 text-center">
              <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mb-5">
                <i className="ri-heart-line text-3xl text-rose-300"></i>
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-2">No saved templates yet</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-6">
                Click the heart icon on any template card to save it here for quick access later.
              </p>
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-sm font-bold rounded-full hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-layout-grid-line text-sm"></i>Browse Templates
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <i className="ri-search-line text-3xl text-slate-300 mb-3"></i>
              <p className="text-sm font-semibold text-slate-600">No matches for &ldquo;{searchQuery}&rdquo;</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 text-xs text-rose-500 hover:text-rose-700 cursor-pointer transition-colors"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {filtered.map(template => (
                <div
                  key={template.id}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-rose-300 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
                >
                  {/* Thumbnail row */}
                  <div className="relative h-32 overflow-hidden bg-slate-100">
                    <img
                      src={template.thumbnail}
                      alt={template.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                    {/* Action buttons on hover */}
                    <div className="absolute bottom-0 left-0 right-0 flex gap-2 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={e => { e.stopPropagation(); onPreview(template); }}
                        className="flex-1 py-1.5 bg-white/90 backdrop-blur-sm rounded-xl text-xs font-bold text-slate-700 flex items-center justify-center gap-1 cursor-pointer hover:bg-white transition-colors whitespace-nowrap"
                      >
                        <i className="ri-eye-line text-xs"></i>Preview
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); onUse(); }}
                        className="flex-1 py-1.5 bg-rose-500 rounded-xl text-xs font-bold text-white flex items-center justify-center gap-1 cursor-pointer hover:bg-rose-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-magic-line text-xs"></i>Use
                      </button>
                    </div>

                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex items-center gap-1.5">
                      <span className="px-2 py-0.5 bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-bold rounded-lg">
                        {template.category}
                      </span>
                      {template.badge && (
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-lg ${badgeColors[template.badge]}`}>
                          {template.badge}
                        </span>
                      )}
                    </div>

                    {/* Unfavorite button */}
                    <button
                      onClick={e => { e.stopPropagation(); onUnfavorite(template.id); }}
                      className="absolute top-2 right-2 w-7 h-7 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer hover:bg-rose-50 transition-colors"
                      title="Remove from favorites"
                    >
                      <i className="ri-heart-fill text-rose-500 text-sm"></i>
                    </button>
                  </div>

                  {/* Info row */}
                  <div
                    className="px-3 py-2.5"
                    onClick={() => onPreview(template)}
                  >
                    <p className="text-sm font-bold text-slate-900 truncate group-hover:text-rose-600 transition-colors">
                      {template.name}
                    </p>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-layout-grid-line text-slate-300"></i>
                          {template.slideCount} slides
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {template.colorPalette.slice(0, 4).map((c, i) => (
                          <div key={i} className="w-3.5 h-3.5 rounded-full border border-white" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {savedTemplates.length > 0 && (
          <div className="px-4 py-4 border-t border-slate-100 flex-shrink-0 bg-slate-50">
            <p className="text-xs text-slate-500 text-center mb-3">
              Favorites are saved to your browser. Sign in to sync across devices.
            </p>
            <button
              onClick={onUse}
              className="w-full py-2.5 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-sm font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line mr-1.5"></i>Sign In to Sync Favorites
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
